import { useEffect, useRef } from 'react';

/**
 * "Already completed" notice.
 *
 * Opens when the user lands on a Reader surah they've already marked
 * 100% read. It replaces the confetti + CompletionModal celebration
 * (which is reserved for the *fresh* moment of completion) so revisits
 * feel calm rather than re-triumphant.
 *
 * Three actions:
 *   - Primary: Continue to next surah (or "Browse all surahs" on #114)
 *   - Secondary: Redo this Surah — resets all read marks and restarts
 *     at ayah 1. Reset happens via a mutation in the parent; we just
 *     surface a pending state on the button so the user knows we're
 *     working.
 *   - Tertiary: Stay on this ayah (close)
 *
 * Visual idiom is intentionally quieter than CompletionModal — a soft
 * pulsing check ring instead of a bouncing trophy, no sparkle dots, no
 * shimmer sweep. The signal is "we remember" not "we're celebrating
 * again". Matches the Revision-Mode pill on the StudyProgressCard.
 */
export function AlreadyCompletedModal({
  open,
  surahName,
  surahNumber,
  totalAyahs,
  isResetting,
  onContinue,
  onRedo,
  onClose,
}: {
  open: boolean;
  surahName: string | undefined;
  surahNumber: number;
  totalAyahs: number;
  /** True while the Redo mutation is in flight — disables buttons and
   *  swaps the Redo label so the user knows it's working. */
  isResetting: boolean;
  /** Primary CTA. Receives the *next* surah number, or null if this
   *  was Surah 114 (parent should route to the surah index then). */
  onContinue: (nextSurahNumber: number | null) => void;
  /** Secondary CTA. Parent runs the reset mutation; on success it
   *  closes the modal and points the URL back to ayah 1 of this surah. */
  onRedo: () => void;
  onClose: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isResetting) {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isResetting, onClose]);

  if (!open) return null;

  const isLastSurah = surahNumber >= 114;
  const nextSurahNumber = isLastSurah ? null : surahNumber + 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="already-completed-modal-title"
      className="
        fixed inset-0 z-50 flex items-center justify-center
        bg-night-950/80 backdrop-blur-sm
        motion-safe:animate-in motion-safe:fade-in
      "
      onClick={(e) => {
        if (e.target === e.currentTarget && !isResetting) onClose();
      }}
    >
      <style>{CHECK_KEYFRAMES}</style>

      <div
        className="
          relative mx-4 w-full max-w-md
          overflow-hidden rounded-3xl
          border border-beam/25 bg-night-800
          p-8 text-center
          shadow-[0_0_60px_-16px_rgba(0,229,255,0.28)]
        "
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-beam/15 blur-3xl"
        />

        {/* Calm check ring — softly pulses instead of bouncing in. */}
        <div className="relative mx-auto mb-4 h-16 w-16">
          <span
            aria-hidden="true"
            className="check-halo absolute inset-[-6px] rounded-full bg-beam/10"
          />
          <span
            aria-hidden="true"
            className="check-ring absolute inset-0 rounded-full border border-beam/40 bg-beam/10 shadow-[inset_0_0_18px_rgba(0,229,255,0.22)]"
          />
          <span
            aria-hidden="true"
            className="
              check-icon relative flex h-full w-full items-center justify-center
              text-beam
              drop-shadow-[0_0_8px_rgba(0,229,255,0.45)]
            "
          >
            <CheckIcon className="h-8 w-8" />
          </span>
        </div>

        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-beam">
          Already Complete
        </p>
        <h2
          id="already-completed-modal-title"
          className="font-display text-2xl font-extrabold text-night-50"
        >
          {surahName ?? 'Surah'}
        </h2>
        <p className="mt-2 text-sm text-night-200">
          You&rsquo;ve already marked all {totalAyahs} ayahs of this surah as
          read. Move on to the next one, or redo this surah from the start.
        </p>

        <div className="mt-7 space-y-3">
          {/* Primary — continue to next surah. */}
          <button
            ref={primaryRef}
            type="button"
            onClick={() => onContinue(nextSurahNumber)}
            disabled={isResetting}
            className="
              w-full rounded-lg
              bg-beam px-4 py-3 text-sm font-semibold uppercase tracking-wide text-beam-ink
              transition-colors hover:brightness-110
              disabled:cursor-not-allowed disabled:opacity-60
            "
          >
            {isLastSurah ? 'Browse all surahs →' : 'Continue to next surah →'}
          </button>

          {/* Secondary — redo. Mildly cautious styling (amber-tinted
              outline) so the user understands this clears their progress
              for this surah. */}
          <button
            type="button"
            onClick={onRedo}
            disabled={isResetting}
            className="
              w-full rounded-lg
              border border-amber-400/40 bg-amber-400/5 px-4 py-3
              text-sm font-semibold uppercase tracking-wide text-amber-200
              transition-colors hover:bg-amber-400/10 hover:text-amber-100
              disabled:cursor-not-allowed disabled:opacity-60
            "
            title="Clears all read marks for this surah and restarts at ayah 1"
          >
            {isResetting ? 'Resetting…' : 'Redo this Surah'}
          </button>

          <button
            type="button"
            onClick={onClose}
            disabled={isResetting}
            className="
              w-full py-2 text-xs font-medium text-night-300
              transition-colors hover:text-night-100
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            Stay on this ayah
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

/**
 * Quiet entrance + ambient pulse for the check ring. No overshoot, no
 * sparkles — this modal is "we remember", not "you did it!".
 *
 * Gated behind prefers-reduced-motion: no-preference so a11y users see
 * a static check.
 */
const CHECK_KEYFRAMES = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes check-fade {
    0%   { opacity: 0; transform: scale(0.85); }
    100% { opacity: 1; transform: scale(1);    }
  }

  @keyframes check-halo-pulse {
    0%, 100% { opacity: 0.30; transform: scale(1);    filter: blur(8px);  }
    50%      { opacity: 0.55; transform: scale(1.10); filter: blur(10px); }
  }

  .check-icon {
    animation: check-fade 500ms ease-out both;
  }

  .check-ring {
    animation: check-fade 400ms ease-out both;
  }

  .check-halo {
    animation: check-halo-pulse 3s ease-in-out infinite;
  }
}
`;
